import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { blemishReportFormSchema, type BlemishReportFormValues } from '@ekhodealer/ekho-common';
import {
	Box,
	Button,
	ContentWithTooltip,
	Form,
	Group,
	SkeletonRect,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';
import { parseVideoUrl } from '@ekhodealer/ekho-common/utils';

import { AccordionSection } from '@common/components/AccordionSection';
import Bin24 from '@common/components/icons/Bin24';

import { useBlemishImageUpload } from '../../api/useBlemishImageUpload';
import { useGetInventoryItem } from '../../api/useGetInventoryItem';
import { cosmeticFormTriggerAtom, drawerAtom } from '../../atoms/editDrawerAtoms';

import { BlemishThumbnail } from './BlemishThumbnail';
import { type MediaItem, MediaModal } from './MediaModal';

import './CosmeticReport.scss';

/** Syncs form blemishes, summary, and enabled state to the Jotai drawer atom on every change */
const BlemishAtomSync = () => {
	const blemishes = useWatch({ name: 'blemishes' }) as BlemishReportFormValues['blemishes'];
	const summary = useWatch({ name: 'summary' }) as string | undefined;
	const isEnabled = useWatch({ name: 'isEnabled' }) as boolean;
	const setDrawer = useSetAtom(drawerAtom);
	const { getValues } = useFormContext<BlemishReportFormValues>();
	const isMountedRef = useRef(false);

	useEffect(() => {
		// Skip the initial render — useWatch emits defaultValues on first subscription,
		// which would falsely mark the drawer dirty before the user has made any changes.
		if (!isMountedRef.current) {
			isMountedRef.current = true;
			return;
		}
		// Read blemishes via getValues() not from the useWatch closure — useFieldArray
		// operations (replace/update) can cause useWatch to fire with a stale intermediate
		// snapshot before nested values are fully propagated in RHF's store.
		// getValues() reads synchronously from the store after the operation completes.
		const currentBlemishes = getValues('blemishes');
		setDrawer((prev) => ({
			...prev,
			cosmeticReport: currentBlemishes ?? [],
			cosmeticReportSummary: summary ?? '',
			cosmeticReportEnabled: isEnabled,
			hasUserMadeChanges: true,
		}));
	}, [blemishes, summary, isEnabled, setDrawer, getValues]);

	return null;
};

/** Registers the RHF trigger() function in a Jotai atom so handleSave can force-show errors */
const CosmeticFormTriggerSync = () => {
	const { trigger } = useFormContext<BlemishReportFormValues>();
	const setTrigger = useSetAtom(cosmeticFormTriggerAtom);
	useEffect(() => {
		setTrigger(() => trigger);
		return () => {
			setTrigger(null);
		};
	}, [trigger, setTrigger]);
	return null;
};

const MAX_MEDIA = 3;

interface BlemishEntryCardProps {
	/** Zero-based index of this blemish entry in the array */
	index: number;
	/** Whether this is the first card — shows the photo helper text */
	isFirst: boolean;
	/** Callback to remove the entry at the given index */
	onRemove: (index: number) => void;
	/** Opens the media modal for this blemish */
	onOpenMediaModal: () => void;
}

/** Resolves a YouTube URL to its thumbnail, or returns null for non-YouTube URLs */
const resolveVideoThumbnail = (url: string): string | null => {
	const parsed = parseVideoUrl(url);
	if (parsed?.vendor === 'youtube') return parsed.thumbnailUrl;
	return null;
};

/** A single blemish entry card with location, description, and media management */
const BlemishEntryCard = ({
	index,
	isFirst,
	onRemove,
	onOpenMediaModal,
}: BlemishEntryCardProps) => {
	const images = (useWatch({ name: `blemishes.${index}.images` }) as string[] | undefined) ?? [];
	const videos = (useWatch({ name: `blemishes.${index}.videos` }) as string[] | undefined) ?? [];

	const totalMedia = images.length + videos.length;
	const showAddSlot = totalMedia < MAX_MEDIA;

	return (
		<Box rounding='8' background='secondary' padding='16' className='cosmetic-report__card'>
			<Stack itemsSpacing='2'>
				{/* Card header */}
				<Group itemsAlignX='space-between' itemsAlignY='center' noWrap>
					<Group.Item>
						<Text
							size='bodySmall'
							weight='semibold'
							color='secondary'
							className='cosmetic-report__card-label'>
							{`BLEMISH ${index + 1}`}
						</Text>
					</Group.Item>
					<Group.Item>
						<Button
							variant='ghost'
							size='icon'
							onPress={() => onRemove(index)}
							ariaLabel={`Remove blemish ${index + 1}`}
							className='cosmetic-report__delete-btn'>
							<Bin24 />
						</Button>
					</Group.Item>
				</Group>

				{/* Location */}
				<Form.Field.Text
					schemaField={`blemishes.${index}.location` as 'blemishes.0.location'}
					labelText='Location'
					placeholder='e.g., Left saddlebag, Front fender, Tank - right side'
					required
				/>

				{/* Description — single-line per spec */}
				<Form.Field.Text
					schemaField={`blemishes.${index}.description` as 'blemishes.0.description'}
					labelText='Description'
					placeholder='e.g., Scratch ~2 inches, surface-level'
					required
				/>

				{/* Photos / Video */}
				<Stack itemsSpacing='12'>
					<Text size='footnote'>
						{`Photos / Video (up to ${MAX_MEDIA})`}
					</Text>
					<Group itemsSpacing='8' itemsAlignY='start' itemsAlignX='start'>
						{images.map((url, i) => (
							<Group.Item key={url}>
								<Button
									variant='secondary'
									ariaLabel={`Blemish photo ${i + 1}`}
									className='cosmetic-report__thumb-slot'
									onPress={onOpenMediaModal}>
									<BlemishThumbnail
										publicUrl={url}
										alt={`Blemish ${index + 1} photo ${i + 1}`}
									/>
								</Button>
							</Group.Item>
						))}
						{videos.map((url, i) => {
							const thumbnailUrl = resolveVideoThumbnail(url);
							return (
								<Group.Item key={url}>
									<Button
										variant='secondary'
										ariaLabel={`Blemish video ${i + 1}`}
										className='cosmetic-report__thumb-slot'
										onPress={onOpenMediaModal}>
										{thumbnailUrl ? (
											<BlemishThumbnail
												publicUrl={thumbnailUrl}
												alt={`Blemish ${index + 1} video ${i + 1}`}
											/>
										) : (
											<Text size='footnote' color='secondary'>Video</Text>
										)}
									</Button>
								</Group.Item>
							);
						})}
						{showAddSlot && (
							<Group.Item>
								<Button
									variant='ghost'
									ariaLabel='Add blemish media'
									className='cosmetic-report__thumb-slot cosmetic-report__thumb-slot--empty'
									onPress={onOpenMediaModal}>
									+
								</Button>
							</Group.Item>
						)}
					</Group>
					{isFirst && (
						<Text size='bodySmall' color='secondary'>
							Adding photos increases buyer confidence
						</Text>
					)}
				</Stack>
			</Stack>
		</Box>
	);
};

/** Renders the dynamic blemish entries list — must be rendered inside Form */
const BlemishEntriesField = () => {
	const { control, getValues } = useFormContext<BlemishReportFormValues>();
	const { fields, append, remove, replace } = useFieldArray({ control, name: 'blemishes' });
	const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
	const [activeBlemishIndex, setActiveBlemishIndex] = useState<number | null>(null);
	const [modalInitialItems, setModalInitialItems] = useState<MediaItem[]>([]);
	const { uploadImages, tasks, clearTasks } = useBlemishImageUpload();

	const handleOpenModal = (index: number): void => {
		// Capture images and videos at open time — reading from RHF synchronously
		// avoids stale-closure issues from render-time computation.
		const currentImages = getValues(`blemishes.${index}.images` as 'blemishes.0.images') ?? [];
		const currentVideos = getValues(`blemishes.${index}.videos` as 'blemishes.0.videos') ?? [];
		const imageItems: MediaItem[] = currentImages.map((url, i) => ({
			id: `blemish-img-${i}`,
			url,
			type: 'image' as const,
		}));
		const videoItems: MediaItem[] = currentVideos.map((url, i) => ({
			id: `blemish-vid-${i}`,
			url,
			type: 'video' as const,
		}));
		setModalInitialItems([...imageItems, ...videoItems]);
		setActiveBlemishIndex(index);
		setIsMediaModalOpen(true);
	};

	const handleModalDone = (items: MediaItem[]): void => {
		if (activeBlemishIndex !== null) {
			// Use replace() instead of update() — update() has a known RHF issue where
			// useWatch({ name: 'blemishes' }) in BlemishAtomSync fires with stale nested values,
			// causing the atom to be written with images:[] and the save button to stay disabled.
			// replace() is a root-level operation that reliably notifies parent-level watchers.
			const allBlemishes = getValues('blemishes');
			const images = items.filter((item) => item.type === 'image').map((item) => item.url);
			const videos = items.filter((item) => item.type === 'video').map((item) => item.url);
			const updatedBlemishes = allBlemishes.map((b, i) =>
				i === activeBlemishIndex ? { ...b, images, videos } : b,
			);
			replace(updatedBlemishes);
		}
	};

	const handleModalClose = (): void => {
		clearTasks();
		setIsMediaModalOpen(false);
	};

	const handleAdd = (): void => {
		append({ location: '', description: '', images: [], videos: [] });
	};

	return (
		<Stack itemsSpacing='12'>
			{fields.map((field, index) => (
				<BlemishEntryCard
					key={field.id}
					index={index}
					isFirst={index === 0}
					onRemove={remove}
					onOpenMediaModal={() => handleOpenModal(index)}
				/>
			))}
			<Button
				variant='ghost'
				size='medium'
				onPress={handleAdd}
				className='cosmetic-report__add-blemish'>
				<Text size='heading' weight='semibold' tag='span'>
					+
				</Text>
				{' Add blemish'}
			</Button>
			<MediaModal
				isOpen={isMediaModalOpen}
				onClose={handleModalClose}
				onDone={handleModalDone}
				initialItems={modalInitialItems}
				onFilesAccepted={uploadImages}
				tasks={tasks}
				maxItems={MAX_MEDIA}
			/>
		</Stack>
	);
};

/**
 * Renders the enabled-state-aware form body — must be rendered inside Form.
 * When isEnabled is false, only the toggle is shown.
 */
const CosmeticReportFormContent = () => {
	const isEnabled = useWatch({ name: 'isEnabled' }) as boolean;

	return (
		<Stack itemsSpacing='16'>
			<Group itemsSpacing='4' itemsAlignY='center' itemsAlignX='start'>
				<Group itemsSpacing='4' itemsAlignY='center' itemsAlignX='start'>
					<Text size='bodySmall'>Show on vehicle listing</Text>
					<Text size='bodySmall' color='secondary'>
						(optional)
					</Text>
				</Group>
				<Form.Field.Switch schemaField='isEnabled' togglePosition='right' />
			</Group>

			{isEnabled && (
				<>
					{/* Summary field with tooltip and helper text */}
					<Stack itemsSpacing='8'>
						<Group itemsSpacing='4' itemsAlignX='start' itemsAlignY='center'>
							<Group.Item>
								<Text size='bodySmall' weight='semibold'>
									Summary
								</Text>
							</Group.Item>
							<Group.Item>
								<ContentWithTooltip
									content={
										<Text size='body' color='secondary'>
											ⓘ
										</Text>
									}
									tooltipContent="A brief overview of the vehicle's cosmetic condition"
									tooltipProps={{ placement: 'top' }}
								/>
							</Group.Item>
						</Group>
						<Stack itemsSpacing='2'>
							<Form.Field.TextArea
								schemaField='summary'
								placeholder='e.g., Minor cosmetic wear consistent with age and mileage'
								rows={2}
								maxLength={300}
								showCharCount
							/>
							<Text size='bodySmall' color='secondary'>
								1–2 sentences shown as the header on the VDP
							</Text>
						</Stack>
					</Stack>

					{/* Blemishes section */}
					<Stack itemsSpacing='12'>
						<Text size='bodySmall' weight='semibold'>
							Blemishes
						</Text>
						<BlemishEntriesField />
					</Stack>
				</>
			)}
		</Stack>
	);
};

export const CosmeticReport = () => {
	const { selectedItemId } = useAtomValue(drawerAtom);
	const { data: item, isLoading } = useGetInventoryItem(selectedItemId ?? '');

	const schema = useMemo(() => blemishReportFormSchema, []);

	if (!selectedItemId || isLoading || !item) {
		return (
			<AccordionSection title='Cosmetic report' defaultExpanded>
				<Stack itemsSpacing='12'>
					<SkeletonRect width='100%' height='80px' />
					<SkeletonRect width='120px' height='32px' />
				</Stack>
			</AccordionSection>
		);
	}

	const defaultValues: BlemishReportFormValues = {
		isEnabled: item?.cosmeticReport?.isEnabled ?? true,
		blemishes: item?.cosmeticReport?.blemishes ?? [],
		summary: item?.cosmeticReport?.conditionSummary ?? '',
	};

	const handleSubmit = async (): Promise<{ isSuccess: boolean }> => {
		return { isSuccess: true };
	};

	return (
		<AccordionSection title='Cosmetic report' defaultExpanded className='cosmetic-report'>
			<Form
				schema={schema}
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				listenForServerChanges={false}
				formOptions={{ mode: 'onBlur' }}>
				<BlemishAtomSync />
				<CosmeticFormTriggerSync />
				<CosmeticReportFormContent />
			</Form>
		</AccordionSection>
	);
};
